/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import configuration */
import {
    COMPONENT_OVERVIEW_NODE_COLORS,
    type ComponentOverviewTheme,
} from '@/config/componentOverviewColors';
import { COMPONENT_OVERVIEW_STRUCTURE } from '@/config/componentOverviewStructure';

/* Import libraries */
import {
    createMermaidFlowchart,
    type MermaidFlowchartDefinition,
} from '@/lib/presentation/mermaidDiagram';

export { COMPONENT_OVERVIEW_STRUCTURE } from '@/config/componentOverviewStructure';

const COMPONENT_OVERVIEW_CLASS_DEFINITIONS: Record<
    ComponentOverviewTheme,
    NonNullable<MermaidFlowchartDefinition['classDefinitions']>
> = {
    dark: createComponentOverviewClassDefinitions('dark'),
    light: createComponentOverviewClassDefinitions('light'),
};

function createComponentOverviewClassDefinitions(
    theme: ComponentOverviewTheme,
): NonNullable<MermaidFlowchartDefinition['classDefinitions']> {
    return Object.fromEntries(
        Object.entries(COMPONENT_OVERVIEW_NODE_COLORS[theme]).map(([variant, colors]) => [
            variant,
            `fill:${colors.backgroundColor},stroke:${colors.borderColor},color:${colors.textColor},${variant === 'route' ? 'font-family:monospace,' : ''}stroke-width:1.5px`,
        ]),
    );
}

function createComponentOverviewDiagram(
    definition: MermaidFlowchartDefinition,
    theme: ComponentOverviewTheme = 'light',
): string {
    return createMermaidFlowchart({
        ...definition,
        classDefinitions: COMPONENT_OVERVIEW_CLASS_DEFINITIONS[theme],
    });
}

export const COMPONENT_OVERVIEW_DIAGRAM = createComponentOverviewDiagram(
    COMPONENT_OVERVIEW_STRUCTURE,
);

const COMPONENT_OVERVIEW_ROUTE_GROUPS: NonNullable<MermaidFlowchartDefinition['groups']> =
    COMPONENT_OVERVIEW_STRUCTURE.groups;
const COMPONENT_OVERVIEW_EDGES: MermaidFlowchartDefinition['edges'] =
    COMPONENT_OVERVIEW_STRUCTURE.edges;

export const COMPONENT_OVERVIEW_ROUTE_OPTIONS = Object.values(COMPONENT_OVERVIEW_ROUTE_GROUPS)
    .flatMap((group) => Object.entries(group.nodes))
    .map(([id, node]) => ({
        id,
        label: typeof node === 'string' ? node : node.label,
    }))
    .sort((left, right) => left.label.localeCompare(right.label));

const COMPONENT_OVERVIEW_APP_NODE_IDS = new Set([
    'app',
    'sessionProvider',
    'dashboardLayoutProvider',
    'overviewDisplayProvider',
    'appShell',
    'appNavbar',
    'appFooter',
    'routes',
]);

export function createComponentOverviewRouteDiagram(
    routeId: string,
    theme: ComponentOverviewTheme = 'light',
): string {
    const isKnownRoute = COMPONENT_OVERVIEW_ROUTE_OPTIONS.some(({ id }) => id === routeId);

    if (!isKnownRoute) {
        throw new Error(`Unknown component overview route: ${routeId}`);
    }

    const nodeIds = new Set([...COMPONENT_OVERVIEW_APP_NODE_IDS, routeId]);
    const pendingNodeIds = [routeId];

    while (pendingNodeIds.length > 0) {
        const currentNodeId = pendingNodeIds.pop();

        if (!currentNodeId) {
            continue;
        }

        COMPONENT_OVERVIEW_EDGES[currentNodeId]?.forEach((to) => {
            if (nodeIds.has(to)) {
                return;
            }

            nodeIds.add(to);
            pendingNodeIds.push(to);
        });
    }

    const groups = Object.fromEntries(
        Object.entries(COMPONENT_OVERVIEW_ROUTE_GROUPS)
            .filter(([, group]) =>
                Object.keys(group.nodes).some((id) => nodeIds.has(id)),
            )
            .map(([id, group]) => [
                id,
                {
                    ...group,
                    nodes: Object.fromEntries(
                        Object.entries(group.nodes).filter(([id]) => nodeIds.has(id)),
                    ),
                },
            ])
    );
    const edges = Object.fromEntries(
        Object.entries(COMPONENT_OVERVIEW_EDGES)
            .map(([from, targets]) => [
                from,
                targets.filter((to) => nodeIds.has(from) && nodeIds.has(to)),
            ])
            .filter(([, targets]) => targets.length > 0),
    );

    return createComponentOverviewDiagram({
        direction: COMPONENT_OVERVIEW_STRUCTURE.direction,
        edges,
        groups,
        nodes: Object.fromEntries(
            Object.entries(COMPONENT_OVERVIEW_STRUCTURE.nodes).filter(([id]) => nodeIds.has(id)),
        ),
    }, theme);
}
